import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, formatEUR, apiUrl, formatApiError } from "@/lib/api";
import { useFeatures } from "@/lib/features";
import { toast } from "sonner";
import { Hotel, Users, BedDouble, Calendar, Plus, X, Search, ScanLine, CheckCircle2, LogOut as LogOutIcon, Loader2, Trash2, Sparkles, FileImage, Shield, Send, AlertCircle, BarChart3, Download } from "lucide-react";

const TABS = [
  { id: "reservations", label: "Prenotazioni", icon: Calendar },
  { id: "guests", label: "Clienti", icon: Users },
  { id: "rooms", label: "Camere", icon: BedDouble },
  { id: "alloggiati", label: "Alloggiati Web", icon: Shield },
  { id: "ross", label: "ROSS 1000", icon: BarChart3 },
];

const ROOM_TYPES = {
  single: "Singola", double: "Matrimoniale", triple: "Tripla", quad: "Familiare", suite: "Suite",
};

const DOC_TYPES = {
  id_card: "Carta identità", cie: "CIE elettronica", passport: "Passaporto", driver_license: "Patente",
};

const SCHEDINA_TYPES = {
  "16": "Ospite singolo",
  "17": "Capo famiglia",
  "19": "Capo gruppo",
  "18": "Familiare",
  "20": "Membro gruppo",
};

const CITIZENSHIP = [
  ["IT", "ITALIA"], ["FR", "FRANCIA"], ["DE", "GERMANIA"], ["CH", "SVIZZERA"], ["AT", "AUSTRIA"],
  ["ES", "SPAGNA"], ["GB", "REGNO UNITO"], ["US", "STATI UNITI"], ["RO", "ROMANIA"], ["AL", "ALBANIA"],
  ["MA", "MAROCCO"], ["UA", "UCRAINA"], ["CN", "CINA"], ["PL", "POLONIA"], ["BR", "BRASILE"],
];

function readDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

/** Foto telefono spesso da 4–8 MB: le riduce, altrimenti la richiesta si interrompe e compare solo «Errore OCR». */
function fileToScanPayload(file) {
  const name = file?.name || "";
  const isPdf = file?.type === "application/pdf" || /\.pdf$/i.test(name);
  if (isPdf) {
    return readDataUrl(file).then((dataUrl) => ({
      base64: String(dataUrl).split(",")[1],
      mime: "application/pdf",
      preview: null,
    }));
  }
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      const max = 1600;
      let w = img.naturalWidth || img.width;
      let h = img.naturalHeight || img.height;
      URL.revokeObjectURL(url);
      if (!w || !h) { reject(new Error("Foto non leggibile. Usa un JPG o un PNG.")); return; }
      const scale = Math.min(1, max / Math.max(w, h));
      w = Math.max(1, Math.round(w * scale));
      h = Math.max(1, Math.round(h * scale));
      const canvas = document.createElement("canvas");
      canvas.width = w;
      canvas.height = h;
      canvas.getContext("2d").drawImage(img, 0, 0, w, h);
      const dataUrl = canvas.toDataURL("image/jpeg", 0.82);
      resolve({ base64: dataUrl.split(",")[1], mime: "image/jpeg", preview: dataUrl });
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("Foto non leggibile. Usa un JPG o un PNG."));
    };
    img.src = url;
  });
}

function scanError(e) {
  const msg = String(e?.message || "");
  if (e?.code === "ECONNABORTED" || /timeout/i.test(msg)) return "La lettura ha impiegato troppo tempo. Riprova con la foto del documento.";
  if (!e?.response && msg && !e?.isNetworkError) return msg;
  return formatApiError(e, "Scansione non riuscita. Riprova con la foto del documento.");
}

function pickedOcr(extracted) {
  const out = {};
  Object.entries(extracted || {}).forEach(([k, v]) => {
    if (v != null && v !== "") out[k] = v;
  });
  return out;
}

const STATUS_LABEL = {
  booked: { label: "Prenotato", color: "bg-blue-100 text-blue-800" },
  checked_in: { label: "Check-in", color: "bg-emerald-100 text-emerald-800" },
  checked_out: { label: "Check-out", color: "bg-slate-200 text-slate-600" },
  canceled: { label: "Annullata", color: "bg-red-100 text-red-700" },
};

export default function HotelPage() {
  const [params, setParams] = useSearchParams();
  const { modes } = useFeatures();
  const dedicated = Array.isArray(modes) && modes.includes("alloggio_dedicato") && !modes.includes("bnb");
  const tabs = dedicated ? TABS.filter((t) => t.id === "guests" || t.id === "alloggiati" || t.id === "ross") : TABS;
  const [tab, setTab] = useState(dedicated ? "guests" : "reservations");
  const openTab = (id) => {
    setTab(id);
    setParams(id === "reservations" ? {} : { tab: id }, { replace: true });
  };
  const [rooms, setRooms] = useState([]);
  const [guests, setGuests] = useState([]);
  const [reservations, setReservations] = useState([]);
  const [editGuest, setEditGuest] = useState(null);
  const [editRoom, setEditRoom] = useState(null);
  const [newRes, setNewRes] = useState(null);
  const [search, setSearch] = useState("");
  const [seeding, setSeeding] = useState(false);

  const loadRooms = () => api.get("/hotel/rooms").then(r => setRooms(Array.isArray(r.data) ? r.data : []));
  const loadGuests = (q) => api.get(`/hotel/guests${q ? `?q=${encodeURIComponent(q)}` : ""}`).then(r => setGuests(Array.isArray(r.data) ? r.data : []));
  const loadRes = () => api.get("/hotel/reservations").then(r => setReservations(Array.isArray(r.data) ? r.data : []));

  useEffect(() => { loadRooms(); loadGuests(); loadRes(); }, []);

  useEffect(() => {
    const t = params.get("tab");
    if (t === "guests" || t === "clienti") setTab("guests");
    else if (!dedicated && (t === "rooms" || t === "camere")) setTab("rooms");
    else if (t && tabs.some((x) => x.id === t)) setTab(t);
    else setTab(dedicated ? "guests" : "reservations");
  }, [params, dedicated]);

  const seed = async () => {
    setSeeding(true);
    try {
      const r = await api.post("/hotel/seed-demo");
      if (r.data.skipped) toast.info("Camere già presenti"); else toast.success(`${r.data.created_rooms} camere create`);
      loadRooms();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); } finally { setSeeding(false); }
  };

  const saveGuest = async (g, { close = true } = {}) => {
    try {
      const r = g.id ? await api.patch(`/hotel/guests/${g.id}`, g) : await api.post("/hotel/guests", g);
      toast.success(dedicated ? "Schedina salvata" : "Cliente salvato");
      if (close) setEditGuest(null);
      else if (r.data?.id) setEditGuest(r.data);
      loadGuests();
      return r.data;
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); return null; }
  };

  const delGuest = async (gid) => {
    if (!window.confirm("Eliminare il cliente? Le prenotazioni già registrate restano.")) return;
    await api.delete(`/hotel/guests/${gid}`);
    loadGuests();
  };

  const importDocs = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    let ok = 0;
    for (const file of files) {
      try {
        const scan = await fileToScanPayload(file);
        const ocr = await api.post("/hotel/guests/ocr", { image_base64: scan.base64, mime_type: scan.mime }, { timeout: 90000, skipNetworkToast: true });
        const d = pickedOcr(ocr.data.extracted);
        if (!d.first_name || !d.last_name) {
          toast.error(`${file.name}: nome o cognome non letti`);
          continue;
        }
        const docType = ["id_card", "cie", "passport", "driver_license"].includes(d.document_type) ? d.document_type : "cie";
        await api.post("/hotel/guests", {
          first_name: d.first_name,
          last_name: d.last_name,
          gender: d.gender === "M" || d.gender === "F" ? d.gender : undefined,
          birth_date: d.birth_date || undefined,
          birth_place: d.birth_place || undefined,
          birth_country_iso: d.birth_country_iso || "IT",
          citizenship_iso: d.citizenship_iso || "IT",
          document_type: docType,
          document_number: d.document_number || undefined,
          document_issue_place: d.document_issue_place || undefined,
          document_expiry_date: d.document_expiry_date || undefined,
          document_image_url: scan.preview || undefined,
        });
        ok += 1;
      } catch (e) {
        toast.error(`${file.name}: ${scanError(e)}`);
      }
    }
    if (ok) {
      toast.success(dedicated
        ? `${ok} ${ok === 1 ? "scheda letta" : "schede lette"}. Indica i giorni di permanenza, poi invia ad Alloggiati e ROSS.`
        : `${ok} ${ok === 1 ? "scheda letta" : "schede lette"}. Collega il cliente a una prenotazione, poi invia ad Alloggiati e ROSS.`);
      loadGuests();
    }
  };

  const sendAuthorities = async (g) => {
    if (dedicated && (!g.arrival_date || !g.stay_days)) {
      toast.error("Indica la data di arrivo e i giorni di permanenza (da 1 a 30), poi invia.");
      setEditGuest(g);
      return false;
    }
    if (!window.confirm(`Inviare i dati di ${g.first_name} ${g.last_name} ad Alloggiati Web e ROSS 1000? La permanenza è di ${g.stay_days} giorni.`)) return false;
    try {
      const r = await api.post(`/hotel/guests/${g.id}/send-authorities`);
      const allo = r.data?.alloggiati || {};
      const ross = r.data?.ross || {};
      if (allo.ok) toast.success("Alloggiati Web: schedina inviata");
      else toast.error(allo.detail || allo.errore || "Alloggiati Web non ha accettato i dati");
      if (ross.ok) toast.success("ROSS 1000: permanenza conteggiata nel mese");
      else toast.error(typeof ross.detail === "string" ? ross.detail : "ROSS 1000 non inviato");
      loadRes();
      loadGuests();
      return !!allo.ok;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Invio non riuscito");
      return false;
    }
  };

  const saveRoom = async (r) => {
    try {
      if (r.id) await api.patch(`/hotel/rooms/${r.id}`, r);
      else await api.post("/hotel/rooms", r);
      toast.success("Camera salvata"); setEditRoom(null); loadRooms();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };

  const doCheckin = async (r) => { await api.post(`/hotel/reservations/${r.id}/checkin`); toast.success(`Check-in ${r.code}`); loadRes(); };
  const doCheckout = async (r) => { await api.post(`/hotel/reservations/${r.id}/checkout`); toast.success(`Check-out ${r.code}`); loadRes(); };

  const roomOf = (id) => rooms.find(r => r.id === id);
  const guestOf = (id) => guests.find(g => g.id === id);

  return (
    <div className="p-8 lg:p-12">
      <div className="flex items-start justify-between flex-wrap gap-4 mb-6">
        <div>
          <div className="text-xs font-mono uppercase tracking-[0.25em] text-velta-mute mb-3">{dedicated ? "Alloggio dedicato" : "Modulo Alberghiero"}</div>
          <h1 className="font-heading text-4xl font-bold tracking-tight flex items-center gap-3"><Hotel className="w-9 h-9" /> {dedicated ? "Clienti" : "Gestione Struttura"}</h1>
          <p className="text-velta-mute mt-2">{dedicated
            ? "Scansiona il documento: i campi si compilano da soli. Tu indichi tipo schedina, arrivo e giorni di permanenza, poi invii ad Alloggiati Web e ROSS 1000. I clienti restano salvati."
            : "I clienti sono solo di questa struttura: nome, documento e telefono. Non entrano nelle casse."}</p>
        </div>
        {!dedicated && rooms.length === 0 && (
          <button data-testid="hotel-seed-demo" onClick={seed} disabled={seeding} className="h-11 px-4 bg-[#2F9E77] hover:bg-[#1bb070] disabled:opacity-60 text-white rounded-md font-medium flex items-center gap-2 btn-tactile">
            <Sparkles className="w-4 h-4" /> {seeding ? "Carico…" : "Carica camere demo"}
          </button>
        )}
      </div>

      {/* Tabs */}
      <div className="bg-white border border-velta-border rounded-md p-1 mb-5 inline-flex gap-1">
        {(Array.isArray(tabs) ? tabs : []).map(t => {
          const Icon = t.icon;
          return (
            <button key={t.id} data-testid={`hotel-tab-${t.id}`} onClick={() => openTab(t.id)} className={`px-4 h-10 rounded font-medium text-sm inline-flex items-center gap-2 ${tab === t.id ? "bg-velta-ink text-white" : "text-velta-mute hover:text-velta-ink"}`}>
              <Icon className="w-4 h-4" /> {t.label}
              <span className={`text-[10px] font-mono ${tab === t.id ? "text-white/70" : "text-velta-mute"}`}>
                {t.id === "reservations" ? reservations.length : t.id === "guests" ? guests.length : rooms.length}
              </span>
            </button>
          );
        })}
      </div>

      {tab === "reservations" && !dedicated && (
        <div>
          <div className="flex justify-end mb-3">
            <button data-testid="hotel-new-res" onClick={() => setNewRes({ room_id: rooms[0]?.id, guest_ids: [], check_in_date: new Date().toISOString().slice(0, 10), check_out_date: new Date(Date.now() + 86400000).toISOString().slice(0, 10) })} disabled={!rooms.length || !guests.length} className="h-10 px-4 bg-[#1E2D5B] hover:bg-[#162244] disabled:opacity-40 text-white rounded-md text-sm font-medium inline-flex items-center gap-2 btn-tactile">
              <Plus className="w-4 h-4" /> Nuova prenotazione
            </button>
          </div>
          <div className="bg-white border border-velta-border rounded-md overflow-x-auto pos-scroll">
            <table className="w-full text-sm min-w-[980px]">
              <thead className="bg-velta-bg border-b border-velta-border text-xs font-mono uppercase tracking-widest text-velta-mute text-left">
                <tr><th className="px-4 py-3">Codice</th><th className="px-4 py-3">Camera</th><th className="px-4 py-3">Cliente</th><th className="px-4 py-3">Check-in</th><th className="px-4 py-3">Check-out</th><th className="px-4 py-3 text-right">Totale</th><th className="px-4 py-3">Stato</th><th className="px-4 py-3"></th></tr>
              </thead>
              <tbody>
                {(Array.isArray(reservations) ? reservations : []).map(r => {
                  const room = roomOf(r.room_id);
                  const g = guestOf(r.guest_ids?.[0]);
                  const s = STATUS_LABEL[r.status] || STATUS_LABEL.booked;
                  return (
                    <tr key={r.id} data-testid={`res-row-${r.id}`} className="border-b border-velta-border last:border-0 hover:bg-velta-bg">
                      <td className="px-4 py-3 font-mono font-bold">{r.code}</td>
                      <td className="px-4 py-3">{room ? `${room.number} — ${room.name || ROOM_TYPES[room.room_type]}` : "—"}</td>
                      <td className="px-4 py-3">{g ? `${g.first_name} ${g.last_name}` : "—"}</td>
                      <td className="px-4 py-3 font-mono">{r.check_in_date}</td>
                      <td className="px-4 py-3 font-mono">{r.check_out_date}</td>
                      <td className="px-4 py-3 text-right font-mono font-bold">{formatEUR(r.total_price)}</td>
                      <td className="px-4 py-3"><span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase ${s.color}`}>{s.label}</span></td>
                      <td className="px-4 py-3 text-right">
                        {r.status === "booked" && <button data-testid={`res-checkin-${r.id}`} onClick={() => doCheckin(r)} className="text-xs font-medium text-[#2F9E77] hover:underline inline-flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> Check-in</button>}
                        {r.status === "checked_in" && <button data-testid={`res-checkout-${r.id}`} onClick={() => doCheckout(r)} className="text-xs font-medium text-[#1E2D5B] hover:underline inline-flex items-center gap-1"><LogOutIcon className="w-3 h-3" /> Check-out</button>}
                        {(r.status === "checked_in" || r.status === "booked") && !r.alloggiati_transmitted && (
                          <button
                            data-testid={`res-alloggiati-${r.id}`}
                            onClick={async () => {
                              if (!window.confirm(`Trasmettere ${r.guest_ids?.length || 0} schedina/e ad Alloggiati Web?`)) return;
                              try {
                                const resp = await api.post(`/hotel/alloggiati/transmit/${r.id}`);
                                if (resp.data.ok) toast.success(`Trasmesso: ${resp.data.guests_transmitted} schedine`);
                                else toast.error(`Errore: ${resp.data.errore || resp.data.esito || "vedi log"}`);
                                loadRes();
                              } catch (e) { toast.error(e?.response?.data?.detail || "Errore trasmissione"); }
                            }}
                            className="ml-2 text-xs font-medium text-[#7C3AED] hover:underline inline-flex items-center gap-1"
                          ><Shield className="w-3 h-3" /> Invia Polizia</button>
                        )}
                        {r.alloggiati_transmitted && (
                          <span className="ml-2 text-[10px] text-[#2F9E77] font-medium inline-flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> Trasmesso</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
                {!reservations.length && <tr><td colSpan={8} className="px-4 py-8 text-center text-velta-mute text-sm">Nessuna prenotazione. {(!rooms.length || !guests.length) && "Crea prima camere e clienti."}</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === "guests" && (
        <div>
          <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
            <div className="relative flex-1 max-w-md">
              <Search className="w-4 h-4 absolute left-3 top-3 text-velta-mute" />
              <input data-testid="hotel-guest-search" value={search} onChange={e => { setSearch(e.target.value); loadGuests(e.target.value); }} placeholder="Cerca per nome, documento, telefono…" className="h-10 w-full pl-9 pr-3 border border-velta-border rounded-md text-sm focus:border-velta-ink focus:outline-none" />
            </div>
            <button data-testid="hotel-new-guest" onClick={() => setEditGuest({ first_name: "", last_name: "", document_type: "cie", citizenship_iso: "IT", birth_country_iso: "IT", gender: dedicated ? "" : "M", schedina_tipo: "16", arrival_date: new Date().toISOString().slice(0, 10), stay_days: "" })} className="h-10 px-4 bg-[#1E2D5B] hover:bg-[#162244] text-white rounded-md text-sm font-medium inline-flex items-center gap-2 btn-tactile">
              <Plus className="w-4 h-4" /> Nuovo cliente
            </button>
            <label className="h-10 px-4 bg-white border border-velta-border rounded-md text-sm font-medium inline-flex items-center gap-2 cursor-pointer btn-tactile">
              <ScanLine className="w-4 h-4" /> Importa copie
              <input data-testid="hotel-import-docs" type="file" accept="image/jpeg,image/png,image/webp,application/pdf" multiple className="hidden" onChange={(e) => { importDocs(e.target.files); e.target.value = ""; }} />
            </label>
          </div>
          <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-3">
            {(Array.isArray(guests) ? guests : []).map(g => (
              <div key={g.id} data-testid={`guest-card-${g.id}`} className="bg-white border border-velta-border rounded-md p-4 flex gap-3">
                {g.document_image_url ? <img src={g.document_image_url} alt="doc" className="w-16 h-20 object-cover rounded shrink-0 border" /> : <div className="w-16 h-20 rounded bg-velta-bg flex items-center justify-center shrink-0"><FileImage className="w-6 h-6 text-velta-mute" /></div>}
                <div className="flex-1 min-w-0">
                  <div className="font-heading font-bold truncate">{g.first_name} {g.last_name}</div>
                  <div className="text-xs text-velta-mute">{DOC_TYPES[g.document_type] || g.document_type} · {g.document_number || "—"}</div>
                  <div className="text-xs text-velta-mute">{g.birth_date || "—"} · {g.citizenship_iso || "IT"}</div>
                  {dedicated && (
                    <div className="text-xs text-velta-mute">
                      {g.arrival_date || "arrivo da indicare"} · {g.stay_days ? `${g.stay_days} giorni` : "permanenza da indicare"}
                      {g.alloggiati_transmitted ? " · inviato" : ""}
                    </div>
                  )}
                  <div className="mt-2 flex gap-2 flex-wrap">
                    <button onClick={() => setEditGuest(g)} className="text-xs text-[#1E2D5B] font-medium hover:underline">{dedicated ? "Scheda" : "Modifica"}</button>
                    <button data-testid={`guest-send-${g.id}`} onClick={() => sendAuthorities(g)} className="text-xs text-[#1E2D5B] font-medium hover:underline">Invia ad Alloggiati e ROSS</button>
                    <button onClick={() => delGuest(g.id)} className="text-xs text-velta-error font-medium hover:underline">Elimina</button>
                  </div>
                </div>
              </div>
            ))}
            {!guests.length && <div className="col-span-full text-center text-sm text-velta-mute py-8">Nessun cliente dell&apos;alloggio. Crea il primo: resta solo in questa struttura.</div>}
          </div>
        </div>
      )}

      {tab === "rooms" && !dedicated && (
        <div>
          <div className="flex justify-end mb-3">
            <button data-testid="hotel-new-room" onClick={() => setEditRoom({ number: "", room_type: "double", capacity: 2, base_price_night: 80, active: true })} className="h-10 px-4 bg-[#1E2D5B] hover:bg-[#162244] text-white rounded-md text-sm font-medium inline-flex items-center gap-2 btn-tactile">
              <Plus className="w-4 h-4" /> Nuova camera
            </button>
          </div>
          <div className="grid md:grid-cols-3 gap-3">
            {(Array.isArray(rooms) ? rooms : []).map(r => (
              <div key={r.id} data-testid={`room-card-${r.id}`} className="bg-white border border-velta-border rounded-md p-4">
                <div className="flex items-start justify-between">
                  <div>
                    <div className="text-xs font-mono uppercase tracking-widest text-velta-mute">Camera</div>
                    <div className="font-heading font-black text-2xl">{r.number}</div>
                    <div className="text-sm text-velta-ink">{r.name || ROOM_TYPES[r.room_type]}</div>
                  </div>
                  <div className="text-right">
                    <div className="font-mono font-bold text-lg">{formatEUR(r.base_price_night)}</div>
                    <div className="text-[10px] text-velta-mute">/notte</div>
                  </div>
                </div>
                <div className="flex items-center gap-3 mt-3 text-xs text-velta-mute">
                  <span>{ROOM_TYPES[r.room_type]}</span>
                  <span>· {r.capacity} posti</span>
                </div>
                <div className="mt-2 flex gap-2">
                  <button onClick={() => setEditRoom(r)} className="text-xs text-[#1E2D5B] font-medium hover:underline">Modifica</button>
                </div>
              </div>
            ))}
            {!rooms.length && <div className="col-span-full text-center text-sm text-velta-mute py-8">Nessuna camera. Clicca &quot;Carica camere demo&quot; per iniziare.</div>}
          </div>
        </div>
      )}

      {tab === "alloggiati" && <AlloggiatiTab reservations={reservations} loadRes={loadRes} guestOf={guestOf} roomOf={roomOf} dedicated={dedicated} guests={guests} onSendGuest={sendAuthorities} />}
      {tab === "ross" && <RossTab />}

      {editGuest && (
        <GuestModal
          g={editGuest}
          dedicated={dedicated}
          onSave={(g) => saveGuest(g)}
          onSend={dedicated ? async (g) => {
            const saved = await saveGuest(g, { close: false });
            if (!saved?.id) return false;
            const ok = await sendAuthorities({ ...saved, ...g, id: saved.id });
            if (ok) setEditGuest(null);
            return ok;
          } : null}
          onClose={() => setEditGuest(null)}
        />
      )}
      {editRoom && <RoomModal r={editRoom} onSave={saveRoom} onClose={() => setEditRoom(null)} />}
      {newRes && <ReservationModal r={newRes} rooms={rooms} guests={guests} onSave={async (data) => { try { await api.post("/hotel/reservations", data); toast.success("Prenotazione creata"); setNewRes(null); loadRes(); } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); } }} onClose={() => setNewRes(null)} />}
    </div>
  );
}

function fmtIt(iso) {
  if (!iso || !/^\d{4}-\d{2}-\d{2}/.test(String(iso))) return iso || "";
  const [y, m, d] = String(iso).slice(0, 10).split("-");
  return `${d}/${m}/${y}`;
}

function GuestModal({ g, onSave, onClose, onSend, dedicated = false }) {
  const [f, setF] = useState(g);
  const [ocring, setOcring] = useState(false);
  const [correct, setCorrect] = useState(false);

  const runOCR = async (file) => {
    setOcring(true);
    try {
      const scan = await fileToScanPayload(file);
      const r = await api.post("/hotel/guests/ocr", { image_base64: scan.base64, mime_type: scan.mime }, { timeout: 90000, skipNetworkToast: true });
      const d = pickedOcr(r.data.extracted);
      setF({
        ...f,
        ...d,
        arrival_date: f.arrival_date,
        stay_days: f.stay_days,
        schedina_tipo: f.schedina_tipo || "16",
        document_image_url: scan.preview || f.document_image_url,
      });
      setCorrect(false);
      toast.success(dedicated ? "Documento letto. Indica tipo, arrivo e permanenza, poi invia." : "Dati estratti dal documento");
    } catch (e) { toast.error(scanError(e)); } finally { setOcring(false); }
  };

  const payload = () => ({
    ...f,
    stay_days: f.stay_days ? Math.min(30, Math.max(1, parseInt(f.stay_days, 10) || 0)) : null,
    schedina_tipo: f.schedina_tipo || "16",
    citizenship_iso: (f.citizenship_iso || "IT").toUpperCase(),
  });
  const fromScan = [
    !f.last_name && "cognome",
    !f.first_name && "nome",
    !f.gender && "sesso",
    !f.birth_date && "data di nascita",
    !f.birth_place && "luogo di nascita",
    !f.document_number && "numero documento",
    !f.document_issue_place && "luogo del documento",
  ].filter(Boolean);
  const byHand = [
    !f.arrival_date && "data di arrivo",
    !f.stay_days && "permanenza (1-30 giorni)",
  ].filter(Boolean);
  const missing = dedicated ? [...byHand, ...fromScan] : [];
  const citLabel = (CITIZENSHIP.find(([iso]) => iso === (f.citizenship_iso || "IT")) || [null, f.citizenship_iso || "ITALIA"])[1];
  const hasDoc = !!(f.last_name || f.first_name || f.document_number);
  const showIdentity = correct || (hasDoc && fromScan.length > 0);

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50 overflow-auto" onClick={onClose}>
      <div className="bg-white rounded-md w-full max-w-2xl p-6 my-8" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-5">
          <h3 className="font-heading text-xl font-semibold">{dedicated ? "Schedina Alloggiati e ROSS" : (f.id ? "Modifica cliente" : "Nuovo cliente")}</h3>
          <button onClick={onClose}><X className="w-5 h-5" /></button>
        </div>

        {/* OCR upload */}
        <div className="mb-5 bg-gradient-to-br from-[#1E2D5B]/5 to-[#2F9E77]/5 border border-dashed border-velta-border rounded-md p-4">
          <div className="flex items-start gap-3">
            <ScanLine className="w-6 h-6 text-[#1E2D5B] shrink-0 mt-1" />
            <div className="flex-1">
              <div className="font-medium text-sm">{dedicated ? "1. Scansiona il documento" : "Scansione documento con AI"}</div>
              <div className="text-xs text-velta-mute mb-2">{dedicated
                ? "La scansione compila cognome, nome, nascita, sesso, cittadinanza e documento. Tu indichi solo tipo schedina, arrivo e giorni."
                : "Foto, copia o PDF di CI/CIE/patente/passaporto. L'OCR compila i campi. Gli stessi dati si inviano poi ad Alloggiati Web e ROSS 1000."}</div>
              <label className={`inline-flex items-center gap-2 h-9 px-3 rounded border cursor-pointer text-sm font-medium ${ocring ? "opacity-50 pointer-events-none" : "bg-white border-velta-ink text-velta-ink hover:bg-velta-ink hover:text-white"}`}>
                {ocring ? <Loader2 className="w-4 h-4 animate-spin" /> : <ScanLine className="w-4 h-4" />}
                {ocring ? "Analisi in corso…" : "Scansiona documento"}
                <input data-testid="guest-ocr-input" type="file" accept="image/jpeg,image/png,image/webp,application/pdf" className="hidden" onChange={(e) => e.target.files?.[0] && runOCR(e.target.files[0])} />
              </label>
            </div>
            {f.document_image_url && <img src={f.document_image_url} alt="doc" className="w-20 h-24 object-cover rounded border" />}
          </div>
        </div>

        {dedicated ? (
          <div className="space-y-4">
            {(f.last_name || f.first_name || f.document_number) ? (
              <div className="border border-velta-border rounded-md p-4 bg-velta-bg/40">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="text-[10px] font-mono uppercase tracking-widest text-velta-mute">Dal documento</div>
                    <div className="font-heading font-bold text-lg leading-tight mt-1">{(f.last_name || "—").toUpperCase()} {(f.first_name || "").toUpperCase()}</div>
                    <div className="text-sm text-velta-ink mt-1">
                      {fmtIt(f.birth_date) || "nascita —"} · {f.birth_place || "luogo —"} · {f.gender === "F" ? "Femmina" : f.gender === "M" ? "Maschio" : "sesso —"}
                    </div>
                    <div className="text-sm text-velta-mute mt-0.5">
                      {citLabel} · {DOC_TYPES[f.document_type] || "Documento"} {f.document_number || "—"} · {f.document_issue_place || "rilascio —"}
                    </div>
                  </div>
                  <button type="button" onClick={() => setCorrect((v) => !v)} className="text-xs text-[#1E2D5B] font-medium shrink-0 hover:underline">{correct ? "Chiudi" : "Correggi"}</button>
                </div>
                {fromScan.length > 0 && <p className="text-xs text-amber-700 mt-2">La scansione non ha letto: {fromScan.join(", ")}.</p>}
              </div>
            ) : (
              <div className="flex items-center justify-between gap-3">
                <p className="text-sm text-velta-mute">Dopo la scansione qui compare la scheda letta dal documento.</p>
                <button type="button" onClick={() => setCorrect((v) => !v)} className="text-xs text-[#1E2D5B] font-medium shrink-0 hover:underline">{correct ? "Chiudi" : "Compila a mano"}</button>
              </div>
            )}

            {showIdentity && (
              <div className="space-y-3">
                <div>
                  <label className="block text-xs font-mono uppercase text-velta-mute mb-1">Cittadinanza</label>
                  <select data-testid="g-ci" value={f.citizenship_iso || "IT"} onChange={e => setF({ ...f, citizenship_iso: e.target.value })} className="h-10 w-full px-3 border border-velta-border rounded text-sm">
                    {CITIZENSHIP.map(([iso, label]) => <option key={iso} value={iso}>{label}</option>)}
                  </select>
                </div>
                <ComuneField label="Luogo di nascita" value={f.birth_place} code={f.birth_place_istat} testid="g-bp"
                  onChange={(v) => setF({ ...f, birth_place: v, birth_place_istat: null })}
                  onPick={(s) => setF({ ...f, birth_place: s.name, birth_place_istat: s.code })} />
                <div className="grid grid-cols-2 gap-3">
                  <F l="Cognome" v={f.last_name} onChange={v => setF({ ...f, last_name: v })} t="g-ln" />
                  <F l="Nome" v={f.first_name} onChange={v => setF({ ...f, first_name: v })} t="g-fn" />
                  <F l="Data di nascita" v={f.birth_date} onChange={v => setF({ ...f, birth_date: v })} type="date" t="g-bd" />
                  <div>
                    <label className="block text-xs font-mono uppercase text-velta-mute mb-1">Sesso</label>
                    <select value={f.gender || ""} onChange={e => setF({ ...f, gender: e.target.value })} className="h-10 w-full px-3 border border-velta-border rounded text-sm">
                      <option value="">—</option><option value="M">Maschio</option><option value="F">Femmina</option>
                    </select>
                  </div>
                </div>
                <ComuneField label="Luogo documento" value={f.document_issue_place} code={f.document_issue_place_istat} testid="g-ip"
                  onChange={(v) => setF({ ...f, document_issue_place: v, document_issue_place_istat: null })}
                  onPick={(s) => setF({ ...f, document_issue_place: s.name, document_issue_place_istat: s.code })} />
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-mono uppercase text-velta-mute mb-1">Tipo documento</label>
                    <select value={f.document_type || "id_card"} onChange={e => setF({ ...f, document_type: e.target.value })} className="h-10 w-full px-3 border border-velta-border rounded text-sm">
                      {Object.entries(DOC_TYPES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                    </select>
                  </div>
                  <F l="Numero documento" v={f.document_number} onChange={v => setF({ ...f, document_number: v })} t="g-dn" />
                </div>
              </div>
            )}

            <div>
              <div className="text-[10px] font-mono uppercase tracking-widest text-velta-mute mb-2">2. Solo questi tre, a mano</div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="block text-xs font-mono uppercase text-velta-mute mb-1">Tipo schedina</label>
                  <select data-testid="g-schedina" value={f.schedina_tipo || "16"} onChange={e => setF({ ...f, schedina_tipo: e.target.value })} className="h-10 w-full px-3 border border-velta-border rounded text-sm">
                    {Object.entries(SCHEDINA_TYPES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                  </select>
                </div>
                <F l="Data arrivo" v={f.arrival_date} onChange={v => setF({ ...f, arrival_date: v })} type="date" t="g-arrivo" />
                <F l="Permanenza, giorni" v={f.stay_days} onChange={v => setF({ ...f, stay_days: String(v || "").replace(/\D/g, "").slice(0, 2) })} t="g-giorni" />
              </div>
              <p className="text-xs text-velta-mute mt-2">Tipo già su ospite singolo, arrivo su oggi. La permanenza, da 1 a 30, va sulla schedina Alloggiati e nel mese ROSS.</p>
            </div>

            <button type="button" data-testid="guest-send-schedina" onClick={() => onSend?.(payload())} disabled={missing.length > 0 || !onSend} className="w-full h-12 bg-[#1E2D5B] hover:bg-[#162244] disabled:opacity-40 text-white rounded-md font-medium">Invia ad Alloggiati e ROSS</button>
            <button type="button" data-testid="guest-save" onClick={() => onSave(payload())} disabled={!f.first_name || !f.last_name} className="w-full text-sm text-velta-mute hover:text-velta-ink disabled:opacity-40">Salva senza inviare</button>
          </div>
        ) : (
        <>
        <div className="grid grid-cols-2 gap-3">
          <F l="Nome" v={f.first_name} onChange={v => setF({ ...f, first_name: v })} t="g-fn" />
          <F l="Cognome" v={f.last_name} onChange={v => setF({ ...f, last_name: v })} t="g-ln" />
          <div>
            <label className="block text-xs font-mono uppercase text-velta-mute mb-1">Sesso</label>
            <select value={f.gender || ""} onChange={e => setF({ ...f, gender: e.target.value })} className="h-10 w-full px-3 border border-velta-border rounded text-sm">
              <option value="">—</option><option value="M">Maschio</option><option value="F">Femmina</option>
            </select>
          </div>
          <F l="Data nascita" v={f.birth_date} onChange={v => setF({ ...f, birth_date: v })} type="date" t="g-bd" />
          <ComuneField label="Luogo nascita" value={f.birth_place} code={f.birth_place_istat} testid="g-bp"
            onChange={(v) => setF({ ...f, birth_place: v, birth_place_istat: null })}
            onPick={(s) => setF({ ...f, birth_place: s.name, birth_place_istat: s.code })} />
          <F l="Cittadinanza (ISO2)" v={f.citizenship_iso} onChange={v => setF({ ...f, citizenship_iso: v?.toUpperCase() })} t="g-ci" />
          <div>
            <label className="block text-xs font-mono uppercase text-velta-mute mb-1">Tipo documento</label>
            <select value={f.document_type || "cie"} onChange={e => setF({ ...f, document_type: e.target.value })} className="h-10 w-full px-3 border border-velta-border rounded text-sm">
              {Object.entries(DOC_TYPES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </div>
          <F l="Numero documento" v={f.document_number} onChange={v => setF({ ...f, document_number: v })} t="g-dn" />
          <ComuneField label="Luogo rilascio" value={f.document_issue_place} code={f.document_issue_place_istat} testid="g-ip"
            onChange={(v) => setF({ ...f, document_issue_place: v, document_issue_place_istat: null })}
            onPick={(s) => setF({ ...f, document_issue_place: s.name, document_issue_place_istat: s.code })} />
          <F l="Scadenza documento" v={f.document_expiry_date} onChange={v => setF({ ...f, document_expiry_date: v })} type="date" t="g-de" />
          <F l="Email" v={f.email} onChange={v => setF({ ...f, email: v })} t="g-em" />
          <F l="Telefono" v={f.phone} onChange={v => setF({ ...f, phone: v })} t="g-ph" />
        </div>
        <button data-testid="guest-save" onClick={() => onSave(payload())} disabled={!f.first_name || !f.last_name} className="w-full mt-5 h-12 bg-[#1E2D5B] hover:bg-[#162244] disabled:opacity-40 text-white rounded-md font-medium btn-tactile">Salva cliente</button>
        </>
        )}
      </div>
    </div>
  );
}

// Campo comune con autocomplete dalla tabella ufficiale Questura (codici a 9 caratteri)
function ComuneField({ label, value, code, onChange, onPick, testid }) {
  const [sugg, setSugg] = useState([]);
  const [open, setOpen] = useState(false);
  const search = async (v) => {
    onChange(v);
    if ((v || "").trim().length < 2) { setSugg([]); setOpen(false); return; }
    try {
      const r = await api.get(`/hotel/comuni/search?q=${encodeURIComponent(v)}`);
      setSugg(Array.isArray(r.data.items) ? r.data.items : []);
      setOpen((r.data.items || []).length > 0);
    } catch { setSugg([]); }
  };
  return (
    <div className="relative">
      <label className="block text-xs font-mono uppercase text-velta-mute mb-1">{label}</label>
      <input
        data-testid={testid}
        value={value || ""}
        onChange={(e) => search(e.target.value)}
        onBlur={() => setTimeout(() => setOpen(false), 200)}
        placeholder="Inizia a digitare il comune…"
        className="h-10 w-full px-3 border border-velta-border rounded text-sm"
      />
      {code
        ? <div className="text-[10px] text-emerald-700 font-mono mt-0.5" data-testid={`${testid}-code`}>✓ codice Questura {code}</div>
        : (value ? <div className="text-[10px] text-velta-mute mt-0.5">codice non abbinato — seleziona dai suggerimenti</div> : null)}
      {open && sugg.length > 0 && (
        <div className="absolute z-20 left-0 right-0 top-full mt-1 bg-white border border-velta-border rounded shadow-lg max-h-44 overflow-auto">
          {(Array.isArray(sugg) ? sugg : []).map((s) => (
            <button key={s.code} type="button" data-testid={`${testid}-sugg-${s.code}`}
              className="w-full text-left px-3 py-1.5 text-sm hover:bg-velta-bg"
              onMouseDown={() => { onPick(s); setOpen(false); }}>
              {s.name} {s.province ? <span className="text-velta-mute text-xs">({s.province})</span> : null} <span className="text-[10px] font-mono text-velta-mute">{s.code}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function RoomModal({ r, onSave, onClose }) {
  const [f, setF] = useState(r);
  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50" onClick={onClose}>
      <div className="bg-white rounded-md w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-5">
          <h3 className="font-heading text-xl font-semibold">{f.id ? "Modifica camera" : "Nuova camera"}</h3>
          <button onClick={onClose}><X className="w-5 h-5" /></button>
        </div>
        <div className="space-y-3">
          <F l="Numero" v={f.number} onChange={v => setF({ ...f, number: v })} t="r-num" />
          <F l="Nome (opzionale)" v={f.name} onChange={v => setF({ ...f, name: v })} t="r-name" />
          <div>
            <label className="block text-xs font-mono uppercase text-velta-mute mb-1">Tipo</label>
            <select value={f.room_type} onChange={e => setF({ ...f, room_type: e.target.value })} className="h-10 w-full px-3 border border-velta-border rounded text-sm">
              {Object.entries(ROOM_TYPES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <F l="Posti" v={f.capacity} onChange={v => setF({ ...f, capacity: parseInt(v) || 1 })} type="number" t="r-cap" />
            <F l="Prezzo/notte €" v={f.base_price_night} onChange={v => setF({ ...f, base_price_night: parseFloat(v) || 0 })} type="number" t="r-price" />
          </div>
        </div>
        <button data-testid="room-save" onClick={() => onSave(f)} className="w-full mt-5 h-12 bg-[#1E2D5B] hover:bg-[#162244] text-white rounded-md font-medium btn-tactile">Salva camera</button>
      </div>
    </div>
  );
}

function ReservationModal({ r, rooms, guests, onSave, onClose }) {
  const [f, setF] = useState(r);
  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50" onClick={onClose}>
      <div className="bg-white rounded-md w-full max-w-lg p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-5"><h3 className="font-heading text-xl font-semibold">Nuova prenotazione</h3><button onClick={onClose}><X className="w-5 h-5" /></button></div>
        <div className="space-y-3">
          <div>
            <label className="block text-xs font-mono uppercase text-velta-mute mb-1">Camera</label>
            <select value={f.room_id || ""} onChange={e => setF({ ...f, room_id: e.target.value })} className="h-10 w-full px-3 border border-velta-border rounded text-sm">
              {(Array.isArray(rooms) ? rooms : []).map(r => <option key={r.id} value={r.id}>Camera {r.number} — {r.name || ROOM_TYPES[r.room_type]} ({formatEUR(r.base_price_night)}/notte)</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-mono uppercase text-velta-mute mb-1">Cliente</label>
            <select value={f.guest_ids?.[0] || ""} onChange={e => setF({ ...f, guest_ids: [e.target.value] })} className="h-10 w-full px-3 border border-velta-border rounded text-sm">
              <option value="">— Seleziona —</option>
              {(Array.isArray(guests) ? guests : []).map(g => <option key={g.id} value={g.id}>{g.first_name} {g.last_name}</option>)}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <F l="Check-in" v={f.check_in_date} onChange={v => setF({ ...f, check_in_date: v })} type="date" t="res-ci" />
            <F l="Check-out" v={f.check_out_date} onChange={v => setF({ ...f, check_out_date: v })} type="date" t="res-co" />
          </div>
        </div>
        <button data-testid="res-save" onClick={() => onSave(f)} disabled={!f.room_id || !f.guest_ids?.[0]} className="w-full mt-5 h-12 bg-[#1E2D5B] hover:bg-[#162244] disabled:opacity-40 text-white rounded-md font-medium btn-tactile">Crea prenotazione</button>
      </div>
    </div>
  );
}

function RossTab() {
  const now = new Date();
  const [cfg, setCfg] = useState({ provider: "turismo5_lombardia", region: "Lombardia", istat_code: "", username: "", password: "", api_endpoint: "", enabled: true });
  const [status, setStatus] = useState(null);
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() || 12); // mese scorso di default
  const [preview, setPreview] = useState(null);
  const [log, setLog] = useState([]);
  const [busy, setBusy] = useState(false);

  const loadCfg = async () => {
    try {
      const r = await api.get("/hotel/ross/config");
      setStatus(r.data);
      if (r.data.configured) setCfg((c) => ({ ...c, ...r.data, password: "" }));
    } catch { /* ignore */ }
  };
  const loadLog = () => api.get("/hotel/ross/log").then(r => setLog(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  useEffect(() => { loadCfg(); loadLog(); }, []);

  const save = async () => {
    try { await api.put("/hotel/ross/config", cfg); toast.success("Configurazione salvata"); loadCfg(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Errore salvataggio"); }
  };

  const doPreview = async () => {
    setBusy(true);
    try { const r = await api.get(`/hotel/ross/preview?year=${year}&month=${month}`); setPreview(r.data); }
    catch (e) { toast.error(e?.response?.data?.detail || "Errore anteprima"); } finally { setBusy(false); }
  };

  const doTransmit = async () => {
    if (!window.confirm(`Trasmettere statistiche ${month}/${year} a ROSS 1000?`)) return;
    setBusy(true);
    try {
      const r = await api.post(`/hotel/ross/transmit?year=${year}&month=${month}`);
      if (r.data.log?.mode === "manual_download") toast.info("CSV pronto — scaricalo e caricalo manualmente su Turismo5");
      else if (r.data.ok) toast.success("Trasmesso a ROSS 1000 ✓");
      else toast.error("Errore trasmissione — controlla lo storico");
      loadLog();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); } finally { setBusy(false); }
  };

  const downloadCsv = async () => {
    const token = localStorage.getItem("velta_token");
    const url = apiUrl(`/hotel/ross/csv?year=${year}&month=${month}`);
    const r = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
    if (!r.ok) { toast.error("Errore download"); return; }
    const blob = await r.blob();
    const a = document.createElement("a"); a.href = URL.createObjectURL(blob);
    a.download = `ROSS1000_${year}_${String(month).padStart(2, "0")}.csv`;
    document.body.appendChild(a); a.click(); a.remove();
  };

  const MONTH_NAMES = ["Gen", "Feb", "Mar", "Apr", "Mag", "Giu", "Lug", "Ago", "Set", "Ott", "Nov", "Dic"];

  return (
    <div className="space-y-5 max-w-4xl">
      {/* Config */}
      <div className="bg-white border border-velta-border rounded-md p-6">
        <div className="flex items-center gap-3 mb-4">
          <BarChart3 className="w-6 h-6 text-[#1E2D5B]" />
          <div>
            <h2 className="font-heading text-xl font-bold">ROSS 1000 — Regione Lombardia (Turismo5)</h2>
            <p className="text-xs text-velta-mute">Trasmissione mensile arrivi + presenze per statistiche ISTAT.</p>
          </div>
          {status?.configured && (
            <span className="ml-auto text-[10px] font-bold px-2 py-0.5 rounded bg-blue-100 text-blue-800">CONFIGURATO</span>
          )}
        </div>
        <div className="grid md:grid-cols-2 gap-3 mb-3">
          <F l="Codice ISTAT struttura*" v={cfg.istat_code} onChange={v => setCfg({ ...cfg, istat_code: v })} t="ross-istat" />
          <F l="Regione" v={cfg.region} onChange={v => setCfg({ ...cfg, region: v })} t="ross-region" />
          <F l="Username Turismo5" v={cfg.username} onChange={v => setCfg({ ...cfg, username: v })} t="ross-user" />
          <F l="Password" v={cfg.password} onChange={v => setCfg({ ...cfg, password: v })} type="password" t="ross-pw" />
        </div>
        <F l="Endpoint API (opzionale — se hai URL di trasmissione automatica)" v={cfg.api_endpoint} onChange={v => setCfg({ ...cfg, api_endpoint: v })} t="ross-endpoint" />
        <div className="mt-4 flex gap-2 flex-wrap">
          <button data-testid="ross-save" onClick={save} className="h-10 px-4 bg-[#1E2D5B] text-white rounded font-medium text-sm inline-flex items-center gap-2"><CheckCircle2 className="w-4 h-4" /> Salva</button>
        </div>
        <div className="mt-3 text-xs text-velta-mute bg-blue-50 border border-blue-200 rounded p-3">
          💡 Il <strong>codice ISTAT struttura</strong> viene assegnato dal Comune al momento dell&apos;apertura dell&apos;attività (es. <code>101250100001</code>). Se non lo conosci, chiedilo all&apos;Ufficio Turismo del tuo Comune. Le credenziali <strong>Turismo5</strong> ti sono state fornite quando ti sei registrato al portale della Regione.
        </div>
      </div>

      {/* Trasmissione mese */}
      <div className="bg-white border border-velta-border rounded-md p-6">
        <h3 className="font-heading font-semibold mb-3">Trasmissione mensile</h3>
        <div className="flex items-center gap-3 flex-wrap mb-4">
          <select value={month} onChange={e => setMonth(parseInt(e.target.value))} className="h-10 px-3 border border-velta-border rounded text-sm">
            {(Array.isArray(MONTH_NAMES) ? MONTH_NAMES : []).map((n, i) => <option key={i} value={i + 1}>{n}</option>)}
          </select>
          <select value={year} onChange={e => setYear(parseInt(e.target.value))} className="h-10 px-3 border border-velta-border rounded text-sm">
            {[now.getFullYear() - 1, now.getFullYear(), now.getFullYear() + 1].map(y => <option key={y} value={y}>{y}</option>)}
          </select>
          <button data-testid="ross-preview" onClick={doPreview} disabled={busy || !status?.configured} className="h-10 px-4 bg-white border border-velta-ink text-velta-ink hover:bg-velta-bg rounded font-medium text-sm inline-flex items-center gap-2 disabled:opacity-40">
            <Search className="w-4 h-4" /> Anteprima
          </button>
          <button data-testid="ross-download" onClick={downloadCsv} disabled={!status?.configured} className="h-10 px-4 bg-white border border-velta-border rounded font-medium text-sm inline-flex items-center gap-2 disabled:opacity-40">
            <Download className="w-4 h-4" /> Scarica CSV
          </button>
          <button data-testid="ross-transmit" onClick={doTransmit} disabled={busy || !status?.configured} className="h-10 px-4 bg-[#7C3AED] hover:bg-[#6b30d1] text-white rounded font-medium text-sm inline-flex items-center gap-2 disabled:opacity-40">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            Trasmetti
          </button>
        </div>

        {preview && (
          <div className="bg-velta-bg rounded-md p-4">
            <div className="grid grid-cols-2 gap-3 mb-3">
              <div>
                <div className="text-[10px] font-mono uppercase text-velta-mute">Arrivi totali</div>
                <div className="font-mono font-black text-3xl">{preview.arrivi_totale}</div>
              </div>
              <div>
                <div className="text-[10px] font-mono uppercase text-velta-mute">Presenze (notti)</div>
                <div className="font-mono font-black text-3xl">{preview.presenze_totale}</div>
              </div>
            </div>
            <div className="text-xs">
              <div className="font-mono uppercase text-velta-mute mb-2">Per nazionalità</div>
              <table className="w-full text-xs">
                <thead className="border-b border-velta-border"><tr><th className="text-left py-1">Paese</th><th className="text-right py-1">Arrivi</th><th className="text-right py-1">Presenze</th></tr></thead>
                <tbody>
                  {Object.keys({
                    ...(preview.arrivi_per_nazione && typeof preview.arrivi_per_nazione === "object" ? preview.arrivi_per_nazione : {}),
                    ...(preview.presenze_per_nazione && typeof preview.presenze_per_nazione === "object" ? preview.presenze_per_nazione : {}),
                  }).sort().map(iso => (
                    <tr key={iso} className="border-b border-velta-border last:border-0"><td className="py-1 font-mono">{iso}</td><td className="text-right font-mono">{(preview.arrivi_per_nazione && preview.arrivi_per_nazione[iso]) || 0}</td><td className="text-right font-mono">{(preview.presenze_per_nazione && preview.presenze_per_nazione[iso]) || 0}</td></tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* Storico */}
      <div className="bg-white border border-velta-border rounded-md p-6">
        <h3 className="font-heading font-semibold mb-3">Storico trasmissioni</h3>
        <div className="space-y-1 text-xs">
          {(Array.isArray(log) ? log : []).map(l => (
            <div key={l.id} className={`flex items-center gap-2 p-2 rounded ${l.ok ? "bg-emerald-50" : "bg-red-50"}`}>
              {l.ok ? <CheckCircle2 className="w-4 h-4 text-emerald-600" /> : <AlertCircle className="w-4 h-4 text-red-600" />}
              <span className="font-mono font-bold">{String(l.month).padStart(2, "0")}/{l.year}</span>
              <span className="text-velta-mute">{l.arrivi_totale} arrivi · {l.presenze_totale} presenze</span>
              {l.mode === "manual_download" && <span className="text-[10px] font-bold text-amber-700">DOWNLOAD MANUALE</span>}
              <span className="ml-auto text-velta-mute">{new Date(l.created_at).toLocaleString("it-IT")}</span>
            </div>
          ))}
          {!log.length && <div className="text-velta-mute text-center py-4">Nessuna trasmissione ancora effettuata.</div>}
        </div>
      </div>
    </div>
  );
}

const F = ({ l, v, onChange, type = "text", t }) => (
  <div>
    <label className="block text-xs font-mono uppercase text-velta-mute mb-1">{l}</label>
    <input data-testid={t} type={type} value={v || ""} onChange={e => onChange(e.target.value)} className="h-10 w-full px-3 border border-velta-border rounded text-sm focus:border-velta-ink focus:outline-none" />
  </div>
);

function AlloggiatiTab({ reservations, loadRes, guestOf, roomOf, dedicated = false, guests = [], onSendGuest }) {
  const [cfg, setCfg] = useState({ utente: "", password: "", ws_key: "", sandbox: false, enabled: true });
  const [status, setStatus] = useState(null);
  const [log, setLog] = useState([]);
  const [testing, setTesting] = useState(false);
  const [showGuide, setShowGuide] = useState(false);
  const [tables, setTables] = useState(null);
  const [syncing, setSyncing] = useState(false);

  const loadTables = () => api.get("/hotel/comuni/search").then(r => setTables(r.data)).catch(() => {});

  const loadCfg = async () => {
    try {
      const r = await api.get("/hotel/alloggiati/config");
      setStatus(r.data);
      if (r.data.configured) setCfg((c) => ({ ...c, utente: r.data.utente || "", sandbox: r.data.sandbox || false, enabled: r.data.enabled !== false }));
    } catch { /* ignore */ }
  };
  const loadLog = () => api.get("/hotel/alloggiati/log?limit=20").then(r => setLog(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  useEffect(() => { loadCfg(); loadLog(); loadTables(); }, []);

  const syncTables = async () => {
    setSyncing(true);
    try {
      const r = await api.post("/hotel/alloggiati/sync-tables");
      if (r.data.ok) toast.success(`Tabella comuni aggiornata: ${r.data.count} luoghi ✓`);
      else toast.error(r.data.detail || "Nessun dato ricevuto dalla Questura");
      loadTables();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore sincronizzazione tabelle"); } finally { setSyncing(false); }
  };

  const save = async () => {
    try {
      await api.put("/hotel/alloggiati/config", cfg);
      toast.success("Credenziali salvate");
      loadCfg();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore salvataggio"); }
  };

  const test = async () => {
    setTesting(true);
    try {
      const r = await api.post("/hotel/alloggiati/test", {
        utente: cfg.utente,
        password: cfg.password,
        ws_key: cfg.ws_key,
      });
      if (r.data.ok) toast.success("Connessione a Polizia OK");
      else toast.error(`Test fallito: ${r.data.detail || "credenziali non accettate"}`);
      loadCfg();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore test"); } finally { setTesting(false); }
  };

  return (
    <div className="space-y-5 max-w-4xl">
      {/* Card credenziali */}
      <div className="bg-white border border-velta-border rounded-md p-6">
        <div className="flex items-center gap-3 mb-4">
          <Shield className="w-6 h-6 text-[#1E2D5B]" />
          <div>
            <h2 className="font-heading text-xl font-bold">Servizio Alloggiati Web</h2>
            <p className="text-xs text-velta-mute">Polizia di Stato — trasmissione automatica delle schedine ospiti entro 24h dal check-in.</p>
          </div>
          {status?.configured && (
            <span className={`ml-auto text-[10px] font-bold px-2 py-0.5 rounded ${status.last_test_ok ? "bg-emerald-100 text-emerald-800" : "bg-yellow-100 text-yellow-800"}`}>
              {status.last_test_ok ? "COLLEGATO" : "DA VERIFICARE"}
            </span>
          )}
        </div>

        <div className="grid md:grid-cols-3 gap-3 mb-3">
          <F l="Utente" v={cfg.utente} onChange={v => setCfg({ ...cfg, utente: v })} t="allogg-utente" />
          <F l="Password" v={cfg.password} onChange={v => setCfg({ ...cfg, password: v })} type="password" t="allogg-password" />
          <F l="WsKey" v={cfg.ws_key} onChange={v => setCfg({ ...cfg, ws_key: v })} type="password" t="allogg-wskey" />
        </div>
        <label className="flex items-center gap-2 text-sm mb-4">
          <input data-testid="allogg-sandbox" type="checkbox" checked={cfg.sandbox} onChange={e => setCfg({ ...cfg, sandbox: e.target.checked })} className="w-4 h-4" />
          <span>Ambiente sandbox (test — non trasmette a Polizia reale)</span>
        </label>
        <div className="flex gap-2 flex-wrap">
          <button data-testid="allogg-save" onClick={save} className="h-10 px-4 bg-[#1E2D5B] hover:bg-[#162244] text-white rounded font-medium text-sm inline-flex items-center gap-2">
            <CheckCircle2 className="w-4 h-4" /> Salva credenziali
          </button>
          <button data-testid="allogg-test" onClick={test} disabled={testing || !cfg.utente || !(cfg.password || status?.has_password) || !(cfg.ws_key || status?.has_ws_key)} className="h-10 px-4 bg-white border-2 border-velta-ink text-velta-ink hover:bg-velta-bg rounded font-medium text-sm inline-flex items-center gap-2 disabled:opacity-40">
            {testing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            {testing ? "Test in corso…" : "Testa connessione"}
          </button>
          <button onClick={() => setShowGuide(!showGuide)} className="h-10 px-3 text-xs text-velta-mute hover:text-velta-ink inline-flex items-center gap-1">
            <AlertCircle className="w-4 h-4" /> Come ottenere le credenziali?
          </button>
        </div>

        {showGuide && (
          <div className="mt-4 bg-blue-50 border border-blue-200 rounded p-4 text-sm space-y-2">
            <p className="font-bold text-blue-900">Passi per il cliente:</p>
            <ol className="list-decimal ml-5 text-blue-900 space-y-1 text-xs">
              <li>Vai su <a href="https://alloggiatiweb.poliziadistato.it" target="_blank" rel="noreferrer" className="underline font-medium">alloggiatiweb.poliziadistato.it</a> e accedi con Utente + Password già forniti dalla Questura.</li>
              <li>Vai in menù <strong>Utilità → Gestione Chiave Web Service</strong>.</li>
              <li>Clicca <strong>Genera nuova chiave</strong> (o visualizza esistente) → copia la stringa <strong>WsKey</strong>.</li>
              <li>Torna qui, incolla Utente, Password e WsKey, poi clicca <strong>Salva</strong> e <strong>Testa connessione</strong>.</li>
              <li>Se il test è verde ✓ tutto è pronto — le schedine si trasmettono automaticamente al check-in.</li>
            </ol>
          </div>
        )}
      </div>

      {/* Tabella comuni Questura (codici a 9 caratteri per le schedine) */}
      <div className="bg-white border border-velta-border rounded-md p-6">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h3 className="font-heading font-semibold">Tabella Comuni (codici Questura)</h3>
            <p className="text-xs text-velta-mute mt-0.5">
              {tables?.synced
                ? <>✓ {tables.count} luoghi in archivio · aggiornata il {(tables.updated_at || "").slice(0, 10)} — i codici comune di nascita e rilascio documento vengono compilati automaticamente nelle schedine.</>
                : <>Non ancora scaricata — senza tabella i codici comune restano vuoti e la Questura può rifiutare le schedine. Servono credenziali salvate e testate.</>}
            </p>
          </div>
          <button data-testid="allogg-sync-tables" onClick={syncTables} disabled={syncing || !status?.configured}
            className="h-10 px-4 bg-white border-2 border-velta-ink text-velta-ink hover:bg-velta-bg rounded font-medium text-sm inline-flex items-center gap-2 disabled:opacity-40">
            {syncing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Shield className="w-4 h-4" />}
            {syncing ? "Scaricamento…" : tables?.synced ? "Aggiorna tabella" : "Scarica dalla Questura"}
          </button>
        </div>
      </div>

      {dedicated && (
        <div className="bg-white border border-velta-border rounded-md p-6">
          <h3 className="font-heading font-semibold mb-3">Clienti da inviare</h3>
          <div className="space-y-2">
            {(Array.isArray(guests) ? guests : []).filter((g) => g.document_number && !g.alloggiati_transmitted).map((g) => (
              <div key={g.id} className="flex items-center gap-3 bg-velta-bg rounded p-3">
                <div className="flex-1">
                  <div className="font-medium text-sm">{g.last_name} {g.first_name}</div>
                  <div className="text-xs text-velta-mute">{g.arrival_date || "arrivo da indicare"} · {g.stay_days ? `${g.stay_days} giorni` : "permanenza da indicare"} · {g.document_number}</div>
                </div>
                <button type="button" onClick={() => onSendGuest?.(g)} disabled={!status?.configured} className="h-9 px-3 bg-[#7C3AED] hover:bg-[#6b30d1] disabled:opacity-40 text-white rounded text-xs font-medium">Invia</button>
              </div>
            ))}
            {(Array.isArray(guests) ? guests : []).filter((g) => g.document_number && !g.alloggiati_transmitted).length === 0 && (
              <div className="text-sm text-velta-mute py-4 text-center">Nessun cliente in attesa. Scansiona il documento, indica i giorni e invia.</div>
            )}
          </div>
        </div>
      )}

      {!dedicated && (
      <div className="bg-white border border-velta-border rounded-md p-6">
        <h3 className="font-heading font-semibold mb-3">Prenotazioni da trasmettere</h3>
        <div className="space-y-2">
          {(Array.isArray(reservations) ? reservations : []).filter(r => (r.status === "checked_in" || r.status === "booked") && !r.alloggiati_transmitted).map(r => {
            const room = roomOf(r.room_id);
            const g = guestOf(r.guest_ids?.[0]);
            return (
              <div key={r.id} className="flex items-center gap-3 bg-velta-bg rounded p-3">
                <div className="flex-1">
                  <div className="font-mono font-bold text-sm">{r.code}</div>
                  <div className="text-xs text-velta-mute">{g ? `${g.first_name} ${g.last_name}` : "—"} · Camera {room?.number} · Check-in {r.check_in_date} · {r.guest_ids?.length} ospit{r.guest_ids?.length === 1 ? "e" : "i"}</div>
                </div>
                <button
                  onClick={async () => {
                    if (!window.confirm(`Trasmettere ${r.guest_ids?.length} schedina/e a Polizia?`)) return;
                    try {
                      const resp = await api.post(`/hotel/alloggiati/transmit/${r.id}`);
                      if (resp.data.ok) toast.success(`✓ Trasmesso: ${resp.data.guests_transmitted} schedine`);
                      else toast.error(`✗ ${resp.data.errore || resp.data.esito || "errore"}`);
                      loadRes(); loadLog();
                    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
                  }}
                  disabled={!status?.configured}
                  className="h-9 px-3 bg-[#7C3AED] hover:bg-[#6b30d1] disabled:opacity-40 text-white rounded text-xs font-medium inline-flex items-center gap-1"
                >
                  <Shield className="w-3 h-3" /> Invia
                </button>
              </div>
            );
          })}
          {(Array.isArray(reservations) ? reservations : []).filter(r => (r.status === "checked_in" || r.status === "booked") && !r.alloggiati_transmitted).length === 0 && (
            <div className="text-sm text-velta-mute py-4 text-center">Nessuna prenotazione in attesa. Le trasmissioni avvengono automaticamente al check-in.</div>
          )}
        </div>
      </div>
      )}

      {/* Storico */}
      <div className="bg-white border border-velta-border rounded-md p-6">
        <h3 className="font-heading font-semibold mb-3">Storico trasmissioni</h3>
        <div className="space-y-1 text-xs">
          {(Array.isArray(log) ? log : []).map(l => (
            <div key={l.id} className={`flex items-center gap-2 p-2 rounded ${l.ok ? "bg-emerald-50" : "bg-red-50"}`}>
              {l.ok ? <CheckCircle2 className="w-4 h-4 text-emerald-600" /> : <AlertCircle className="w-4 h-4 text-red-600" />}
              <span className="font-mono font-bold">{l.reservation_code}</span>
              <span className="text-velta-mute">{l.guests_count} schedine</span>
              <span className="ml-auto text-velta-mute">{new Date(l.created_at).toLocaleString("it-IT")}</span>
            </div>
          ))}
          {!log.length && <div className="text-velta-mute text-center py-4">Nessuna trasmissione ancora effettuata.</div>}
        </div>
      </div>
    </div>
  );
}
